import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useCanWrite } from "../auth/AuthProvider";
import { BatchResult } from "../components/BatchResult";
import { FilterChips } from "../components/FilterChips";
import { Modal } from "../components/Modal";
import { QuotaContext, RESOURCE_LABEL } from "../components/QuotaContext";
import { QuotaDecisionModal } from "../components/QuotaDecisionModal";
import { SelectionBar } from "../components/SelectionBar";
import { Alert, Button, Card, EmptyState, Field, PageHeader, Tag, Textarea, buttonStyles, cx } from "../components/ui";
import { formatRelative } from "../lib/format";
import { useDecideQuotaRequests, useOrg, useQuotaRequests } from "../lib/queries";
import type { BatchOutcome, QuotaRequest } from "../lib/types";

/**
 * The queue of requests to raise a limit, and the history of decided ones.
 *
 * Here rather than only on each organization's page because a queue that is
 * only visible from inside the organization that filled it is a queue
 * nobody empties -- the badge on Organizations counts this list. Each row
 * carries what a decision needs (age, credit, paid, what is held, earlier
 * requests) so most are decided from here without opening the organization.
 * `?org=` narrows to one organization, which is the link from its page.
 * Deciding is a superadmin's: an approval changes what a tenant can put on
 * the fleet.
 *
 * Waiting rows can be decided in a batch -- approved as asked, or denied with
 * one note -- for the mornings the queue is twenty requests that need no
 * thought. One that should get less than it asked for is still decided on
 * its own, where the number can be changed.
 */
const STATUS_TONE = { requested: "warn", approved: "good", denied: "bad" } as const;
const STATUS_LABEL = { requested: "Waiting", approved: "Approved", denied: "Denied" } as const;
const overdue = (requestedAt: string) => Date.now() - new Date(requestedAt).getTime() > 2 * 24 * 60 * 60_000;

type View = "waiting" | "decided";
type Decided = "approved" | "denied";

export function QuotaRequestsPage() {
  const canWrite = useCanWrite();
  const [params] = useSearchParams();
  const orgId = params.get("org") ?? undefined;
  const org = useOrg(orgId ?? "");
  const [view, setView] = useState<View>("waiting");
  const [filter, setFilter] = useState<Decided | "">("");
  const [deciding, setDeciding] = useState<{ request: QuotaRequest; decision: "approved" | "denied" } | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const waiting = useQuotaRequests("requested", 100, orgId);
  const decided = useQuotaRequests(filter || undefined, 50, orgId, view === "decided");
  const history = decided.data?.data.filter((r) => r.status !== "requested") ?? [];
  const list = view === "waiting" ? waiting : decided;

  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [batch, setBatch] = useState<"approved" | "denied" | null>(null);
  const [result, setResult] = useState<{ outcomes: BatchOutcome[]; done: string } | null>(null);
  const waitingRows = waiting.data?.data ?? [];
  const chosen = waitingRows.filter((request) => selected.has(request.id));
  const allChosen = waitingRows.length > 0 && chosen.length === waitingRows.length;
  const toggle = (id: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const selectable = canWrite && view === "waiting";

  return (
    <div className="space-y-6">
      <PageHeader
        title="Limit requests"
        description="Organizations asking for a higher limit. Every request is decided by a person; the organization is emailed the decision."
        action={
          orgId ? (
            <Link to="/organizations/limit-requests" className={buttonStyles("secondary", "sm")}>
              Show all organizations
            </Link>
          ) : undefined
        }
      />

      {orgId && (
        <Alert tone="info">
          Showing requests from{" "}
          <Link to={`/organizations/${orgId}`} className="font-medium underline underline-offset-2">
            {org.data?.name ?? "this organization"}
          </Link>{" "}
          only.
        </Alert>
      )}

      {done && <Alert tone="success">{done}</Alert>}
      {result && <BatchResult outcomes={result.outcomes} noun="request" done={result.done} />}

      <Card>
        <div className="flex flex-wrap items-center gap-3 border-b border-line px-5 py-3">
          {/* A segmented pair, as on Organizations: two states of the page, and the selected one reads as where you are. */}
          <div role="group" aria-label="Which requests" className="flex rounded-md border border-line p-0.5">
            {(["waiting", "decided"] as const).map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={view === option}
                onClick={() => {
                  setView(option);
                  setSelected(new Set());
                }}
                className={cx(
                  "rounded px-3 py-1 text-xs transition-colors duration-150",
                  view === option ? "bg-raised text-fg" : "text-fg-subtle hover:text-fg",
                )}
              >
                {option === "waiting" ? `Waiting${waiting.data ? ` (${waiting.data.total})` : ""}` : "Decided"}
              </button>
            ))}
          </div>
          {view === "decided" && (
            <FilterChips<Decided>
              value={filter}
              onChange={setFilter}
              options={[
                { value: "approved", label: "Approved" },
                { value: "denied", label: "Denied" },
              ]}
              ariaLabel="Decision"
            />
          )}
        </div>

        {list.error && (
          <div className="p-5">
            <Alert>{list.error.message}</Alert>
          </div>
        )}

        {view === "waiting" && waiting.data && waiting.data.data.length === 0 && (
          <EmptyState title="Nothing waiting" description="New requests appear here as soon as they are sent." />
        )}
        {view === "decided" && decided.data && history.length === 0 && <EmptyState title="No decisions yet" />}

        {/* Covers what is loaded and says so -- the whole queue up to a
            hundred, which is as many as it has ever held. */}
        {selectable && waitingRows.length > 1 && (
          <label className="flex cursor-pointer items-center gap-3 border-b border-line px-5 py-2.5 text-sm text-fg-muted">
            <input
              type="checkbox"
              className="size-4 accent-accent"
              checked={allChosen}
              ref={(el) => {
                if (el) el.indeterminate = chosen.length > 0 && !allChosen;
              }}
              onChange={() => setSelected(allChosen ? new Set() : new Set(waitingRows.map((r) => r.id)))}
            />
            Select all {waitingRows.length} waiting
          </label>
        )}

        <ul className="divide-y divide-line">
          {(view === "waiting" ? (waiting.data?.data ?? []) : history).map((request) => (
            <li key={request.id} className={cx("flex gap-3 px-5 py-4", selected.has(request.id) && "bg-raised/60")}>
              {selectable && (
                <input
                  type="checkbox"
                  aria-label={`Select ${request.organization.name}'s request`}
                  className="mt-1 size-4 shrink-0 accent-accent"
                  checked={selected.has(request.id)}
                  onChange={() => toggle(request.id)}
                />
              )}
              <div className="min-w-0 flex-1 space-y-2">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <Link to={`/organizations/${request.organization.id}`} className="font-medium text-fg hover:underline">
                        {request.organization.name}
                      </Link>
                      <span className="text-sm text-fg-muted">
                        asks for <span className="font-mono text-fg">{request.requestedLimit}</span>{" "}
                        {RESOURCE_LABEL[request.resource]}
                        <span className="text-fg-subtle"> (now {request.currentLimit})</span>
                      </span>
                      {request.status !== "requested" && (
                        <Tag tone={STATUS_TONE[request.status]}>
                          {request.status === "approved" && request.grantedLimit !== null && request.grantedLimit !== request.requestedLimit
                            ? `Approved at ${request.grantedLimit}`
                            : STATUS_LABEL[request.status]}
                        </Tag>
                      )}
                    </div>
                    <QuotaContext request={request} />
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-2">
                    <span className={`text-xs ${request.status === "requested" && overdue(request.requestedAt) ? "text-warn" : "text-fg-subtle"}`}>
                      {request.status === "requested"
                        ? `waiting ${formatRelative(request.requestedAt).replace(" ago", "")}`
                        : `decided ${formatRelative(request.decidedAt)}`}
                    </span>
                    {canWrite && request.status === "requested" && (
                      <div className="flex gap-2">
                        <Button size="sm" variant="secondary" onClick={() => setDeciding({ request, decision: "denied" })}>
                          Deny
                        </Button>
                        <Button size="sm" onClick={() => setDeciding({ request, decision: "approved" })}>
                          Approve
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
                <p className="text-[13px] text-fg-muted">
                  “{request.reason}”
                  {request.requestedBy && <span className="text-fg-subtle"> — {request.requestedBy}</span>}
                </p>
                {request.decisionNote && (
                  <p className="text-[13px] text-fg-muted">
                    <span className="text-fg-subtle">Note:</span> {request.decisionNote}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ul>
      </Card>

      <QuotaDecisionModal
        request={deciding?.request ?? null}
        decision={deciding?.decision ?? "approved"}
        onClose={() => setDeciding(null)}
        onDecided={(request, decision, granted) =>
          setDone(
            decision === "approved"
              ? `Approved — ${request.organization.name} can now have ${granted} ${RESOURCE_LABEL[request.resource]}. Their admins have been emailed.`
              : `Denied — ${request.organization.name}'s admins have been emailed your note.`,
          )
        }
      />

      {selectable && (
        <SelectionBar
          count={chosen.length}
          noun="request"
          onClear={() => setSelected(new Set())}
          action={
            <span className="flex items-center gap-2">
              <Button variant="secondary" size="sm" onClick={() => setBatch("denied")}>
                Deny…
              </Button>
              <Button size="sm" onClick={() => setBatch("approved")}>
                Approve as asked…
              </Button>
            </span>
          }
        >
          {chosen.filter((request) => !request.organization.paid).length > 0
            ? `${chosen.filter((request) => !request.organization.paid).length} never paid`
            : "all have paid before"}
        </SelectionBar>
      )}

      <BatchDecision
        decision={batch}
        requests={chosen}
        onClose={() => setBatch(null)}
        onDone={(decision, outcomes) => {
          setBatch(null);
          setSelected(new Set());
          setDone(null);
          setResult({ outcomes, done: decision === "approved" ? "approved as asked" : "denied" });
        }}
      />
    </div>
  );
}

/**
 * The same decision on every selected request.
 *
 * Approval grants each what it asked for -- the batch is for the requests
 * that need no haggling -- so the list shows every number being granted,
 * against what each organization has now, before the button does. The note
 * goes to every organization's admins as written; on a denial it is required,
 * as on one, because "no" with no reason is a ticket the next day.
 */
function BatchDecision({
  decision,
  requests,
  onClose,
  onDone,
}: {
  decision: "approved" | "denied" | null;
  requests: QuotaRequest[];
  onClose: () => void;
  onDone: (decision: "approved" | "denied", outcomes: BatchOutcome[]) => void;
}) {
  const decide = useDecideQuotaRequests();
  const [note, setNote] = useState("");
  const count = requests.length;
  const approving = decision === "approved";

  function close() {
    if (decide.isPending) return;
    decide.reset();
    onClose();
  }

  return (
    <Modal
      open={decision !== null}
      onClose={close}
      dismissable={!decide.isPending}
      title={`${approving ? "Approve" : "Deny"} ${count} request${count === 1 ? "" : "s"}`}
      description={
        approving
          ? "Each organization gets exactly what it asked for, and its admins are emailed."
          : "Nothing changes for any of them. Each organization's admins are emailed your note."
      }
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (!decision) return;
          decide.mutate(
            { ids: requests.map((request) => request.id), decision, ...(note.trim() ? { note: note.trim() } : {}) },
            {
              onSuccess: (response) => {
                setNote("");
                onDone(decision, response.data);
              },
            },
          );
        }}
        className="space-y-4"
      >
        {decide.error && <Alert>{decide.error.message}</Alert>}
        <ul className="max-h-48 divide-y divide-line overflow-y-auto rounded-md border border-line text-sm">
          {requests.map((request) => (
            <li key={request.id} className="flex items-baseline justify-between gap-3 px-3 py-2">
              <span className="min-w-0 truncate text-fg">{request.organization.name}</span>
              <span className="shrink-0 text-xs text-fg-subtle">
                {RESOURCE_LABEL[request.resource]} {request.currentLimit} →{" "}
                <span className={cx("font-mono", approving ? "text-fg" : "text-fg-subtle line-through")}>
                  {request.requestedLimit}
                </span>
              </span>
            </li>
          ))}
        </ul>
        <Field
          label={approving ? "Note (optional)" : "Note"}
          hint="Sent to every one of them as written."
          htmlFor="batch-quota-note"
        >
          <Textarea
            id="batch-quota-note"
            rows={3}
            maxLength={2000}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            required={!approving}
            placeholder={approving ? undefined : "Limits past the default are for accounts with a payment on file."}
          />
        </Field>
        <div className="flex justify-end gap-3">
          <Button type="button" variant="ghost" onClick={close} disabled={decide.isPending}>
            Cancel
          </Button>
          <Button type="submit" variant={approving ? "primary" : "danger"} loading={decide.isPending}>
            {approving ? "Approve" : "Deny"} {count}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
